import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PButtonPure, PText } from "@porsche-design-system/components-react"
import {
	colorContrastMedium,
	proseTextXsStyle,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type {
	InventoryListFilterValue,
	ListingQuality,
	InventoryListFilterView,
	SelectOptionCategorized,
} from "@slfinrtl/ui-bff-routes"
import { Fragment, useCallback, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import {
	SelectField,
	CheckboxGroupField,
	InputField,
	Input,
	RadioGroupField,
	VehicleListFilter as Filter,
	CheckboxField,
} from "src/components"
import { useConfig } from "src/hooks"
import { inventoryListFilterMessages } from "./inventoryListFilter.messages"

const messages = defineMessages({
	showMore: {
		defaultMessage: "Show all {count} options",
		id: "inventoryListFilters.showMore",
	},
})

type ValueType = string[] | string | boolean | null | number | undefined
export interface InventoryListFiltersProps {
	onChange?: (
		value: InventoryListFilterValue,
		key: keyof InventoryListFilterValue,
		type?: "select" | "deselect",
		changedValue?: string,
	) => void
	value: InventoryListFilterValue
	view: InventoryListFilterView
}

export const InventoryListFilters = ({ onChange, value, view }: InventoryListFiltersProps) => {
	const { formatMessage } = useIntl()
	const { distance } = useDealerContext()
	const { vatDeductibleAvailable } = useConfig()
	const { getValues } = useFormContext<InventoryListFilterValue>()
	const engineAndTransmissionActiveCount =
		(value.minEnginePower ? 1 : 0) +
		(value.driveTrains?.length ?? 0) +
		(value.engineTypes?.length ?? 0) +
		(value.transmissions?.length ?? 0)

	const handleFilterChange = useCallback(
		(key: keyof InventoryListFilterValue) =>
			(_val: ValueType, changedValue?: string, type?: "select" | "deselect") => {
				onChange?.(getValues(), key, type, changedValue)
			},
		[onChange, getValues],
	)

	const listingQualityOptions = useMemo(
		() =>
			[
				{ label: formatMessage(inventoryListFilterMessages.goodQuality), value: "GOOD_QUALITY" },
				{ label: formatMessage(inventoryListFilterMessages.mediumQuality), value: "MEDIUM_QUALITY" },
				{ label: formatMessage(inventoryListFilterMessages.lowQuality), value: "LOW_QUALITY" },
			] satisfies { label: string; value: ListingQuality }[],
		[formatMessage],
	)

	return (
		<>
			{view.dealershipIds && (
				<Filter
					initiallyOpen={false}
					title={formatMessage(inventoryListFilterMessages.location)}
					activeFilterCount={value.dealershipIds?.length}
				>
					<CheckboxGroupField
						items={view.dealershipIds}
						name="dealershipIds"
						onChange={handleFilterChange("dealershipIds")}
					/>
				</Filter>
			)}
			<Filter
				title={formatMessage(inventoryListFilterMessages.condition)}
				activeFilterCount={(value.vehicleTypes?.length ?? 0) + (value.accidentFree ? 1 : 0)}
			>
				<CheckboxGroupField
					items={view.vehicleTypes}
					name="vehicleTypes"
					onChange={handleFilterChange("vehicleTypes")}
				/>
				<StyledCheckboxField
					name="accidentFree"
					onChange={handleFilterChange("accidentFree")}
					label={formatMessage(inventoryListFilterMessages.accidentFree)}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.modelSeries)}
				activeFilterCount={value.modelSeries ? 1 : 0}
				initiallyOpen
			>
				<SelectField
					options={view.modelSeries}
					name="modelSeries"
					onChange={handleFilterChange("modelSeries")}
					emptyLabel={formatMessage(inventoryListFilterMessages.modelSeriesAllModels)}
					emptyValue=""
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.salesStatus)}
				activeFilterCount={value.salesStatus?.length}
			>
				<CheckboxGroupField
					items={view.salesStatus}
					name="salesStatus"
					onChange={handleFilterChange("salesStatus")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.stockStatus)}
				activeFilterCount={value.stockStatus?.length}
			>
				<CheckboxGroupField
					items={view.stockStatus}
					name="stockStatus"
					onChange={handleFilterChange("stockStatus")}
				/>
			</Filter>
			{view.listingVisibility && (
				<Filter
					title={formatMessage(inventoryListFilterMessages.listingVisibility)}
					activeFilterCount={value.listingVisibility?.length}
				>
					<CheckboxGroupField
						items={view.listingVisibility}
						name="listingVisibility"
						onChange={handleFilterChange("listingVisibility")}
					/>
				</Filter>
			)}
			<Filter
				title={formatMessage(inventoryListFilterMessages.listingQuality)}
				activeFilterCount={value.listingQuality?.length}
			>
				<CheckboxGroupField
					items={listingQualityOptions}
					name="listingQuality"
					onChange={handleFilterChange("listingQuality")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.modelVariants)}
				activeFilterCount={value.derivatives?.length}
			>
				{!value.modelSeries ? (
					<NoSelectionText color="contrast-medium">
						<FormattedMessage {...inventoryListFilterMessages.noModelVariants} />
					</NoSelectionText>
				) : (
					<SearchableCategorizedSelectOptions
						categories={view.derivatives}
						name="derivatives"
						onChange={handleFilterChange("derivatives")}
					/>
				)}
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.modelGenerations)}
				activeFilterCount={value.modelGenerations?.length}
			>
				{!value.modelSeries ? (
					<NoSelectionText color="contrast-medium">
						<FormattedMessage {...inventoryListFilterMessages.noModelVariants} />
					</NoSelectionText>
				) : (
					<CheckboxGroupField
						items={view.modelGenerations}
						name="modelGenerations"
						onChange={handleFilterChange("modelGenerations")}
					/>
				)}
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.bodyType)}
				activeFilterCount={value.bodyTypes?.length}
			>
				<CheckboxGroupField
					items={view.bodyTypes}
					name="bodyTypes"
					onChange={handleFilterChange("bodyTypes")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.engineAndTransmission)}
				activeFilterCount={engineAndTransmissionActiveCount}
			>
				<InputField
					type="number"
					debounced={500}
					label={formatMessage(inventoryListFilterMessages.minEnginePower)}
					name="minEnginePower"
					onChange={handleFilterChange("minEnginePower")}
				/>
				<EnginePowerUnit>
					<RadioGroupField
						withoutPreselection
						horizontal
						name="enginePowerUnit"
						options={[
							{ label: formatMessage(inventoryListFilterMessages.kilowatt), value: "kW" },
							{ label: formatMessage(inventoryListFilterMessages.horsepower), value: "PS" },
						]}
						onChange={handleFilterChange("enginePowerUnit")}
					/>
				</EnginePowerUnit>
				<StyledCheckboxGroupField
					items={view.transmissions}
					name="transmissions"
					label={formatMessage(inventoryListFilterMessages.transmissions)}
					onChange={handleFilterChange("transmissions")}
				/>
				<StyledCheckboxGroupField
					items={view.driveTrains}
					name="driveTrains"
					label={formatMessage(inventoryListFilterMessages.driveTrains)}
					onChange={handleFilterChange("driveTrains")}
				/>
				<StyledCheckboxGroupField
					items={view.engineTypes}
					name="engineTypes"
					label={formatMessage(inventoryListFilterMessages.engineTypes)}
					onChange={handleFilterChange("engineTypes")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.equipment)}
				activeFilterCount={value.equipmentTags?.length}
			>
				<SearchableCategorizedSelectOptions
					categories={view.equipmentTags}
					name="equipmentTags"
					onChange={handleFilterChange("equipmentTags")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.exteriorColor)}
				activeFilterCount={value.exteriorColors?.length}
			>
				<CheckboxGroupField
					items={view.exteriorColors}
					name="exteriorColors"
					onChange={handleFilterChange("exteriorColors")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.interiorColor)}
				activeFilterCount={value.interiorColors?.length}
			>
				<CheckboxGroupField
					items={view.interiorColors}
					name="interiorColors"
					onChange={handleFilterChange("interiorColors")}
				/>
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.price)}
				activeFilterCount={(value.maxPrice ? 1 : 0) + (value.vatDeductible ? 1 : 0)}
			>
				<InputField
					type="currency"
					debounced={500}
					label={formatMessage(inventoryListFilterMessages.maxPrice)}
					name="maxPrice"
					onChange={handleFilterChange("maxPrice")}
				/>
				{vatDeductibleAvailable && (
					<StyledCheckboxField
						name="vatDeductible"
						label={formatMessage(inventoryListFilterMessages.vatDeductible)}
						onChange={handleFilterChange("vatDeductible")}
					/>
				)}
			</Filter>
			<Filter
				title={formatMessage(inventoryListFilterMessages.mileage)}
				activeFilterCount={value.maxMileage ? 1 : 0}
			>
				<InputField
					unit={distance}
					unitPosition="suffix"
					type="number"
					debounced={500}
					label={formatMessage(inventoryListFilterMessages.maxMileage)}
					name="maxMileage"
					onChange={handleFilterChange("maxMileage")}
				/>
			</Filter>
			{(!!view.minModelYear || !!view.maxModelYear) && (
				<Filter
					title={formatMessage(inventoryListFilterMessages.year)}
					activeFilterCount={(value.minModelYear ? 1 : 0) + (value.maxModelYear ? 1 : 0)}
				>
					<TwoColumnGrid>
						{view.minModelYear && (
							<SelectField
								options={view.minModelYear}
								label={formatMessage(inventoryListFilterMessages.minModelYear)}
								name="minModelYear"
								onChange={handleFilterChange("minModelYear")}
								emptyLabel={formatMessage(commonMessages.all)}
								emptyValue=""
							/>
						)}
						{view.maxModelYear && (
							<SelectField
								options={view.maxModelYear}
								label={formatMessage(inventoryListFilterMessages.maxModelYear)}
								name="maxModelYear"
								onChange={handleFilterChange("maxModelYear")}
								emptyLabel={formatMessage(commonMessages.all)}
								emptyValue=""
							/>
						)}
					</TwoColumnGrid>
				</Filter>
			)}
			{(!!view.minFirstRegistrationDate || !!view.maxFirstRegistrationDate) && (
				<Filter
					title={formatMessage(inventoryListFilterMessages.firstRegistration)}
					activeFilterCount={
						(value.minFirstRegistrationDate ? 1 : 0) + (value.maxFirstRegistrationDate ? 1 : 0)
					}
				>
					<TwoColumnGrid>
						{view.minFirstRegistrationDate && (
							<SelectField
								options={view.minFirstRegistrationDate}
								label={formatMessage(inventoryListFilterMessages.minFirstRegistrationDate)}
								name="minFirstRegistrationDate"
								onChange={handleFilterChange("minFirstRegistrationDate")}
								emptyLabel={formatMessage(commonMessages.all)}
								emptyValue=""
							/>
						)}
						{view.maxFirstRegistrationDate && (
							<SelectField
								options={view.maxFirstRegistrationDate}
								label={formatMessage(inventoryListFilterMessages.maxFirstRegistrationDate)}
								name="maxFirstRegistrationDate"
								onChange={handleFilterChange("maxFirstRegistrationDate")}
								emptyLabel={formatMessage(commonMessages.all)}
								emptyValue=""
							/>
						)}
					</TwoColumnGrid>
				</Filter>
			)}
			<Filter
				title={formatMessage(inventoryListFilterMessages.previousOwners)}
				activeFilterCount={value.previousOwners ? 1 : 0}
			>
				<SelectField
					options={previousOwnersOptions}
					label={formatMessage(inventoryListFilterMessages.maxPreviousOwners)}
					name="previousOwners"
					onChange={() => handleFilterChange("previousOwners")}
					emptyLabel={formatMessage(commonMessages.all)}
					emptyValue=""
				/>
			</Filter>
			{view.onlineOrderableStatus && (
				<Filter
					title={formatMessage(inventoryListFilterMessages.onlineOrderableStatus)}
					activeFilterCount={value.onlineOrderableStatus?.length}
				>
					<CheckboxGroupField
						items={view.onlineOrderableStatus}
						name="onlineOrderableStatus"
						onChange={handleFilterChange("onlineOrderableStatus")}
					/>
				</Filter>
			)}
		</>
	)
}

const StyledCheckboxField = styled(CheckboxField)`
	margin-top: ${spacingStaticSm};
`

const addSelectOptionLength = (count: number, { selectOptions }: SelectOptionCategorized) =>
	count + selectOptions.length

const previousOwnersOptions = Array.from(Array(5).keys(), (val) => ({
	label: `${val}`,
	value: `${val}`,
}))

const SEARCHABLE_THRESHOLD = 20

const SearchableCategorizedSelectOptions = (props: {
	categories: SelectOptionCategorized[]
	name: string
	onChange?: (value: string[]) => void
}) => {
	const { categories, name, onChange } = props

	const [search, setSearch] = useState<string | undefined>()
	const [isExpanded, setIsExpanded] = useState(false)

	const { formatMessage } = useIntl()

	const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), [])

	const filteredCategories: SelectOptionCategorized[] = useMemo(() => {
		if (!search || search === "") {
			return categories
		}
		return categories
			.map((category) => ({
				...category,
				selectOptions: category.selectOptions.filter(({ label }) =>
					label.toLowerCase().includes(search.toLowerCase()),
				),
			}))
			.filter((category) => category.selectOptions.length > 0)
	}, [search, categories])

	const filteredOptionsCount: number = useMemo(
		() => filteredCategories.reduce(addSelectOptionLength, 0),
		[filteredCategories],
	)
	const allOptionsCount: number = useMemo(() => categories.reduce(addSelectOptionLength, 0), [categories])

	if (allOptionsCount < SEARCHABLE_THRESHOLD) {
		return <CategorizedSelectOptions categories={categories} name={name} onChange={onChange} />
	}

	const limitedFilteredCategories = limitOptions(filteredCategories)

	return (
		<>
			<Search
				type="search"
				name={`${name}_search`}
				value={search}
				onChange={setSearch}
				placeholder={formatMessage(commonMessages.search)}
			/>
			<CategorizedSelectOptions
				categories={isExpanded ? filteredCategories : limitedFilteredCategories}
				name={name}
				onChange={onChange}
			/>
			{filteredOptionsCount > SEARCHABLE_THRESHOLD && (
				<ShowMoreButton size="sm" underline icon={isExpanded ? "minus" : "plus"} onClick={toggleExpanded}>
					{isExpanded ? (
						<FormattedMessage {...commonMessages.showLess} />
					) : (
						<FormattedMessage {...messages.showMore} values={{ count: filteredOptionsCount }} />
					)}
				</ShowMoreButton>
			)}
		</>
	)
}

const CategorizedSelectOptions = ({
	categories,
	name,
	onChange,
}: {
	categories: SelectOptionCategorized[]
	name: string
	onChange?: (value: string[]) => void
}) => {
	return categories.map(({ categoryName, selectOptions }, i) => (
		<Fragment key={i}>
			<CategoryName $marginTop={i > 0}>{categoryName}</CategoryName>
			<CheckboxGroupField shouldUnregister={false} items={selectOptions} name={name} onChange={onChange} />
		</Fragment>
	))
}

const limitOptions = (categories: SelectOptionCategorized[], maxOptions = 10) => {
	let remaining = maxOptions

	return categories.reduce((acc, item) => {
		if (remaining <= 0) {
			return acc
		}

		const { selectOptions, ...rest } = item
		const limitedOptions = selectOptions.slice(0, remaining)
		remaining -= limitedOptions.length

		acc.push({ ...rest, selectOptions: limitedOptions })
		return acc
	}, [] as SelectOptionCategorized[])
}

const CategoryName = styled("div", { shouldForwardProp })<{ $marginTop: boolean }>`
	${proseTextXsStyle};
	color: ${colorContrastMedium};
	text-transform: uppercase;
	letter-spacing: 2px;
	margin: ${({ $marginTop }) => `${$marginTop ? spacingStaticMd : 0} 0 ${spacingStaticSm}`};
`

const ShowMoreButton = styled(PButtonPure)`
	margin-top: ${spacingStaticSm};
`

const Search = styled(Input)`
	margin-bottom: ${spacingStaticMd};
`

const TwoColumnGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: ${spacingStaticMd};
`

const NoSelectionText = styled(PText)`
	margin: ${spacingStaticSm} 0;
`

const EnginePowerUnit = styled.div`
	margin-top: ${spacingStaticMd};
`

const StyledCheckboxGroupField = styled(CheckboxGroupField)`
	margin-top: ${spacingStaticMd};
`
